import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Autocomplete, Chip, TextField } from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { MessageTestingMessage } from '@trinity/chat/types/message-testing';
import { Body } from '@trinity/chat-components/components/Modal/Body.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Header } from '@trinity/chat-components/components/Modal/Header.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import type { SegmentOption, TestableMessage } from './types';

const ALLOWED_SEGMENT_NAMES = [
  'Elderberry Dabbler',
  'Elderberry Dominant',
  'Grape Adopter',
  'Defender of Banana + Fig + Lemon',
  'Doublet Disciple',
] as const;

interface MessageTestingModalProps {
  open: boolean;
  onClose: () => void;
  selectedMessages: TestableMessage[];
  onRunStarted: (runIdentifier: string) => void;
  /** When set, auto-selects the segmentation run whose sessionId matches this value. */
  initialSegmentationSessionId?: string;
  /** When set (together with initialSegmentationSessionId), auto-selects matching segments. */
  initialSegmentIdentifiers?: string[];
}

interface SegmentationRunOption {
  id: number;
  sessionId?: string | null;
  projectTitle?: string;
  runKey?: string;
}

export function MessageTestingModal({
  open,
  onClose,
  selectedMessages,
  onRunStarted,
  initialSegmentationSessionId,
  initialSegmentIdentifiers,
}: MessageTestingModalProps): React.JSX.Element {
  const apiClient = useChatApiClient();

  const [selectedRunId, setSelectedRunId] = useState<number | null>(null);
  const [selectedSegments, setSelectedSegments] = useState<SegmentOption[]>([]);
  const [objective, setObjective] = useState('');
  const [autoSelectedRun, setAutoSelectedRun] = useState(false);
  const [autoSelectedSegments, setAutoSelectedSegments] = useState(false);

  // Reset auto-selection flags when the modal closes
  useEffect(() => {
    if (!open) {
      setAutoSelectedRun(false);
      setAutoSelectedSegments(false);
    }
  }, [open]);

  const { data: segmentationRuns = [] } = useQuery({
    queryKey: ['segmentation-runs', { status: 'complete' }],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get('/segmentation-runs', {
        params: { status: 'complete', take: 100 },
      });
      return (response.data.runs || []) as SegmentationRunOption[];
    },
    enabled: open,
  });

  // Auto-select the segmentation run when initialSegmentationSessionId is provided
  useEffect(() => {
    if (!autoSelectedRun && open && initialSegmentationSessionId && segmentationRuns.length > 0) {
      const matchingRun = segmentationRuns.find((run) => run.sessionId === initialSegmentationSessionId);
      if (matchingRun) {
        setSelectedRunId(matchingRun.id);
        setAutoSelectedRun(true);
      }
    }
  }, [autoSelectedRun, open, initialSegmentationSessionId, segmentationRuns]);

  const { data: selectedRunData } = useQuery({
    queryKey: ['segmentation-run', selectedRunId],
    queryFn: async () => {
      const response = await apiClient.authenticatedClient.get(`/segmentation-runs/${selectedRunId}`);
      return response.data.run;
    },
    enabled: !!selectedRunId,
  });

  const segmentOptions: SegmentOption[] = useMemo(() => {
    const segments = selectedRunData?.segments || [];
    return segments
      .filter((s: { identifier: string; name: string }) =>
        ALLOWED_SEGMENT_NAMES.some((n) => n.toLowerCase() === s.name.toLowerCase())
      )
      .map((s: { identifier: string; name: string }) => ({
        identifier: s.identifier,
        name: s.name,
      }));
  }, [selectedRunData]);

  // Auto-select segments when initialSegmentIdentifiers is provided and segment options are loaded
  useEffect(() => {
    if (
      !autoSelectedSegments &&
      open &&
      initialSegmentIdentifiers &&
      initialSegmentIdentifiers.length > 0 &&
      segmentOptions.length > 0
    ) {
      const matchingSegments = segmentOptions.filter((seg) =>
        initialSegmentIdentifiers.includes(seg.identifier)
      );
      if (matchingSegments.length > 0) {
        setSelectedSegments(matchingSegments);
        setAutoSelectedSegments(true);
      }
    }
  }, [autoSelectedSegments, open, initialSegmentIdentifiers, segmentOptions]);

  const brand = selectedMessages[0]?.brand ?? 'Elderberry';

  const { mutate: startRun, isPending } = useMutation({
    mutationFn: async () => {
      const messages: MessageTestingMessage[] = selectedMessages.map((m) => ({
        id: m.id,
        code: m.code,
        value: m.body,
        category: m.category,
      }));

      return apiClient.messageTesting.startRun({
        segmentIdentifiers: selectedSegments.map((s) => s.identifier),
        messages,
        brand,
        objective: objective || undefined,
      });
    },
    onSuccess: (data) => {
      onRunStarted(data.runIdentifier);
      onClose();
    },
  });

  const handleSubmit = useCallback(() => {
    startRun();
  }, [startRun]);

  const isValid = selectedSegments.length > 0 && selectedMessages.length > 0;

  // Find the matching run object for the Autocomplete value
  const selectedRunOption = useMemo(
    () => segmentationRuns.find((r) => r.id === selectedRunId) ?? null,
    [segmentationRuns, selectedRunId]
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Header>Simulate Message Testing</Header>
      <Body>
        <HintText>
          For a more conversational simulated experience, navigate to a segment's profile and use the
          "Synthetic Audience" tab.
        </HintText>
        <Section>
          <Label>Selected Messages ({selectedMessages.length})</Label>
          <MessageList>
            {selectedMessages.map((msg) => (
              <MessageItem key={msg.id}>
                <MessageTitle>{msg.displayTitle}</MessageTitle>
                <MessageBody>{msg.body}</MessageBody>
              </MessageItem>
            ))}
          </MessageList>
        </Section>

        <Section>
          <Label>Segmentation Run</Label>
          <Autocomplete
            options={segmentationRuns}
            value={selectedRunOption}
            getOptionLabel={(option: SegmentationRunOption) =>
              option.projectTitle || option.runKey || `Run ${option.id}`
            }
            onChange={(_e, value) => {
              setSelectedRunId(value?.id ?? null);
              setSelectedSegments([]);
              setAutoSelectedSegments(false);
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField {...params} placeholder="Select a segmentation run..." size="small" />
            )}
            size="small"
          />
        </Section>

        <Section>
          <Label>Segments</Label>
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={segmentOptions}
            getOptionLabel={(option) => option.name}
            value={selectedSegments}
            onChange={(_e, value) => setSelectedSegments(value)}
            disabled={!selectedRunId}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => {
                const { key, ...rest } = getTagProps({ index });
                return <Chip key={key} label={option.name} size="small" {...rest} />;
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={selectedRunId ? 'Select segments...' : 'Select a run first'}
                size="small"
              />
            )}
            size="small"
            isOptionEqualToValue={(option, value) => option.identifier === value.identifier}
          />
        </Section>

        <Section>
          <Label>Objective</Label>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={4}
            value={objective}
            onChange={(e) => setObjective(e.target.value)}
            placeholder="Describe the objective for this message testing simulation..."
            size="small"
          />
        </Section>
      </Body>
      <ButtonSet>
        <ButtonOutlined onClick={onClose}>Cancel</ButtonOutlined>
        <ButtonActivityIndicator disabled={!isValid || isPending} onClick={handleSubmit} loading={isPending}>
          Start Simulation
        </ButtonActivityIndicator>
      </ButtonSet>
    </Modal>
  );
}

const Section = styled.div`
  margin-bottom: 16px;
`;

const HintText = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 16px;
  line-height: 1.4;
`;

const Label = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 6px;
`;

const MessageList = styled.div`
  max-height: 150px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  padding: 8px;
  background: #f9fafb;
`;

const MessageItem = styled.div`
  padding: 6px 0;
  border-bottom: 1px solid #eee;

  &:last-child {
    border-bottom: none;
  }
`;

const MessageTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #1a1a1a;
`;

const MessageBody = styled.div`
  font-size: 11px;
  color: #666;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
